import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toggleSidebar } from "../store/uiSlice";
import {
  LayoutDashboard,
  Ship,
  ShieldCheck,
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import SidebarMenu from "./SidebarMenu";
import { getCompanyMenuAssignments } from "../services/menuService";

const Sidebar = () => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});
  const collapsed = useSelector((state) => state.ui.isSidebarCollapsed);
  const dispatch = useDispatch();
  const location = useLocation();

  // Get user data
  const user = JSON.parse(localStorage.getItem("userData"));
  const companyId = user?.company?._id;
  const role = user?.role;

  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const response = await getCompanyMenuAssignments(companyId);
        // const transformedMenus = transformMenuData(response.menus, role);

        // console.log("transformedMenus", transformedMenus);

        // setMenuData(transformedMenus);
        let transformedMenus = transformMenuData(response.menus, role);

        // Swap EU MRV and EU ETS in Compliance submenu
        transformedMenus = transformedMenus.map(menu => {
          if (menu.title === "Compliance") {
            const swappedSubmenu = menu.submenuItems.map(item => {
              // if (item.label === "EU MRV") return { ...item, label: "EU ETS" };
              // if (item.label === "EU ETS") return { ...item, label: "EU MRV" };
              return item;
            });
            return { ...menu, submenuItems: swappedSubmenu };
          }
          return menu;
        });

        setMenuData(transformedMenus);

      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setLoading(false);
      }
    };

    if (companyId && role) {
      fetchMenuData();
    }
  }, [companyId, role]);

  const transformMenuData = (apiMenus, userRole) => {
    if (!apiMenus || !userRole || !Array.isArray(apiMenus)) return [];

    const roleField = userRole.toLowerCase();
    const isSuperAdmin = userRole === "super-admin";

    if (isSuperAdmin) {
      return apiMenus
        .map((parent) => {
          const iconMap = {
            "Fleet Overview": <LayoutDashboard size={22} />,
            "Vessel Overview": <Ship size={22} />,
            "Compliance": <ShieldCheck size={22} />,
            "Admin Panel": <SlidersHorizontal size={22} />,
          };

          return {
            key: parent._id,
            title: parent.title,
            icon: iconMap[parent.title] || <LayoutDashboard size={22} />,
            to: parent.routePath,
            submenuItems: parent.children
              ? parent.children
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((child) => ({
                    to: child.routePath,
                    label: child.title,
                  }))
              : [],
          };
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    // For non-super-admin roles
    const allMenus = apiMenus.reduce((acc, parent) => {
      acc.push(parent);
      if (parent.children && Array.isArray(parent.children)) {
        acc.push(...parent.children);
      }
      return acc;
    }, []);

    const accessibleChildren = allMenus.filter(
      (menu) => !menu.isParent && menu.isActive && menu[roleField]
    );

    const parentIdsWithAccessibleChildren = new Set(
      accessibleChildren.map((child) => child.parentId).filter(Boolean)
    );

    const filteredMenus = allMenus.filter((menu) => {
      if (menu.isParent) {
        return (
          parentIdsWithAccessibleChildren.has(menu._id) ||
          (menu.isActive && menu[roleField])
        );
      }
      return accessibleChildren.some((c) => c._id === menu._id);
    });

    return apiMenus
      .filter((parent) => {
        const hasAccessibleChildren =
          parent.children &&
          parent.children.some((child) =>
            filteredMenus.some((m) => m._id === child._id)
          );
        return (
          filteredMenus.some((m) => m._id === parent._id) ||
          hasAccessibleChildren
        );
      })
      .map((parent) => {
        const children = filteredMenus.filter(
          (child) => child.parentId && child.parentId === parent._id
        );

        const iconMap = {
          "Fleet Overview": <LayoutDashboard size={22} />,
          "Vessel Overview": <Ship size={22} />,
          "Compliance": <ShieldCheck size={22} />,
          "Admin Panel": <SlidersHorizontal size={22} />,
        };

        return {
          key: parent._id,
          title: parent.title,
          icon: iconMap[parent.title] || <LayoutDashboard size={22} />,
          to: parent.routePath,
          submenuItems: children
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((child) => ({
              to: child.routePath,
              label: child.title,
            })),
        };
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  useEffect(() => {
    if (menuData.length > 0) {
      const activeMenuKey = menuData.find((menu) =>
        menu.submenuItems?.some((item) => location.pathname.startsWith(item.to))
      )?.key;

      if (activeMenuKey) {
        setExpandedMenus({ [activeMenuKey]: true });
      }
    }
  }, [location.pathname, menuData]);

  const toggleMenu = (key) => {
    setExpandedMenus((prev) => (prev[key] ? {} : { [key]: true }));
  };

  if (loading) {
    return (
      <aside className="h-full px-4 transition-all duration-300 text-white">
        <div className="text-sm rounded-t-xl bg-gradient-to-b from-[#d1e5fc] to-white p-4">
          Loading menus...
        </div>
      </aside>
    );
  }

  return (
    <aside className="h-full px-4 transition-all duration-300 text-white">
      <nav className="text-sm rounded-t-xl bg-gradient-to-b from-[#d1e5fc] to-white">
        {menuData.map((menu, index) => (
          <SidebarMenu
            key={menu.key}
            collapsed={collapsed}
            title={menu.title}
            to={menu.to}
            icon={menu.icon}
            expanded={!!expandedMenus[menu.key]}
            onToggle={() => toggleMenu(menu.key)}
            submenuItems={menu.submenuItems}
            isFirst={index === 0}
          />
        ))}
      </nav>
      <div className="hide-below-1025 flex justify-end mt-6 relative z-10">
        <button
          onClick={() => dispatch(toggleSidebar())}
          className="fixed bottom-4 w-10 h-10 flex items-center justify-center rounded-full bg-[#1a325d] hover:bg-[#27ade3] transition-all duration-300 shadow-md z-10"
        >
          {collapsed ? (
            <ChevronRight size={24} className="text-white" />
          ) : (
            <ChevronLeft size={24} className="text-white" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
